import React, { useState } from 'react';
import { useLanguage } from '@/context/language-context';
import { 
  User, Calendar, MessageSquare, Image, DollarSign, 
  Shield, BarChart2, HelpCircle, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import EscortDashboardNavigation from './EscortDashboardNavigation';
import { useAuth } from '@/context/auth';
import { useConversations } from '@/hooks/useConversations';

interface EscortDashboardLayoutProps {
  children: React.ReactNode;
}

const EscortDashboardLayout: React.FC<EscortDashboardLayoutProps> = ({ children }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { totalUnreadCount } = useConversations();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="container mx-auto py-4">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Dashboard header for mobile */}
        <div className="block md:hidden w-full border-b pb-4 mb-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{t('escort:dashboard.title')}</h1>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetHeader>
                  <SheetTitle>{t('escort:dashboard.navigation')}</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  <EscortDashboardNavigation totalUnread={totalUnreadCount} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Sidebar for desktop */}
        <div className={`hidden md:block ${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 ease-in-out bg-card rounded-lg shadow-sm h-[calc(100vh-2rem)]`}>
          <div className="p-4 border-b flex items-center justify-between">
            {sidebarOpen && <h2 className="font-semibold">{t('escort:dashboard.title')}</h2>}
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
          <div className="p-4">
            <EscortDashboardNavigation collapsed={!sidebarOpen} totalUnread={totalUnreadCount} />
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 bg-card rounded-lg shadow-sm p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default EscortDashboardLayout;
